<template>
  <div>
    <div class=row>
      <div class="col-6">
        <div>
          <form>
            <div class="form-group">
              <div class="form-check form-check-inline">
                <input class="form-check-input" type="radio" name="inlineRadioOptions" id="inlineRadio1" value="manual" v-model="modeSelection">
                <label class="form-check-label" for="inlineRadio1">Manual</label>
              </div>
              <div class="form-check form-check-inline">
                <input class="form-check-input" type="radio" name="inlineRadioOptions" id="inlineRadio2" value="list" v-model="modeSelection">
                <label class="form-check-label" for="inlineRadio2">List</label>
              </div>
            </div>
            <div class="form-group">
              <div class="row">
                <div class="col">
                  <label for="target-list">Target Name:</label>
                </div>
              </div>
              <select id="target-list" v-if="modeSelection === 'list'" v-model="targetName" class="form-control">
                <option disabled value="">Select a target...</option>
                <option v-for="target in Array.from(targets.keys()).sort()" :value="target">
                  {{ target }} ({{ targets.get(target).avmDesc }} - {{ targets.get(target).averageExposureSeconds
                  }} sec. avg.)
                </option>
              </select>
              <input v-else v-validate="'required'" id="target-name" type="text" v-model="targetName"
                     class="form-control" name="name" placeholder="Target Name">
              <div v-show="errors.has('name')" class="alert alert-danger">{{ errors.first('name') }}</div>
            </div>
            <div class="form-group">
              <label>Right Ascension:</label>
              <input v-if="modeSelection === 'list'" v-model="raSexagesimal" readonly="readonly"
                     placeholder="HH:MM:SS.S" class="form-control">
              <input v-else v-validate="'required|ra'" v-model="raSexagesimal" type="text"
                     placeholder="HH:MM:SS.S" name="ra" class="form-control">
              <div v-show="errors.has('ra')" class="alert alert-danger">{{ errors.first('ra') }}</div>
            </div>
            <div class="form-group">
              <label>Declination:</label>
              <input v-if="modeSelection === 'list'" v-model="decSexagesimal" readonly="readonly"
                     placeholder="DD:MM:SS.S" class="form-control">
              <input v-else v-validate="'required|dec'" v-model="decSexagesimal" type="text"
                     placeholder="DD:MM:SS.S" name="dec" class="form-control">
              <div v-show="errors.has('dec')" class="alert alert-danger">{{ errors.first('dec') }}</div>
            </div>
            <div class="form-group">
              <label class="pr-2">Filters:</label>
              <div v-for="exposure in exposures" :key="exposure.filter" class="form-check form-check-inline">
                <input class="form-check-input filter-check" type="checkbox" value="" :id="exposure.filterText + 'FilterCheck'" data-toggle="collapse" :href="'#' + exposure.filterText + '-collapse'" role="button" aria-expanded="false" :aria-controls="exposure.filterText + '-collapse'">
                <label class="form-check-label" :for="exposure.filterText + 'FilterCheck'">
                  {{ exposure.filterText }}
                </label>
              </div>
              <a href="#" onclick="return false;" tabindex="0" data-toggle="popover" data-trigger="focus" title="Info" :data-content="filtersHelpText" class="float-right">Info</a>
            </div>
            <div v-for="exposure in exposures" :key="exposure.filter" class="form-group collapse filter-collapse" :id="exposure.filterText + '-collapse'">
              <label>Exposure Time ({{ exposure.filterText }}):</label>
              <input v-if="modeSelection === 'list'" v-model="exposure.exposureTime" placeholder="Seconds"
                     class="form-control">
              <input v-else v-validate="'required|numeric'" :name="'exposureSeconds' + exposure.filter" type="number" class="form-control"
                     :id="'exposure-time-input' + exposure.filter" placeholder="Seconds" v-model="exposure.exposureTime">
              <div v-show="errors.has('expTime' + exposure.filter)" class="alert alert-danger">{{ errors.first('expTime' + exposure.filter) }}</div>
            </div>
          </form>
          <div v-if="totalTime > 0" class="total-time-text">
            This observation will take about {{ totalTime }} seconds to complete.
          </div>
          <div v-else class="total-time-text">&nbsp;</div>
          <transition name="exposure" mode="out-in">
            <div v-if="buttonType === 'go'" class="tooltip-wrapper " data-toggle="tooltip"
                 :title="buttonFeedback" data-placement="right">
              <button type="button" @click="go()" class="btn btn-success btn-block"
                      :disabled="isGoButtonDisabled || inputsAreNotAllFilledIn || !canObserve">
                Go!
              </button>
            </div>
            <button v-else @click="cancel()" class="btn btn-primary btn-block " data-toggle="tooltip"
                    :title="buttonFeedback" data-placement="right" :disabled="isCancelButtonDisabled">
              Cancel
            </button>
          </transition>
          <div class="alert alert-danger mt-3" role="alert" :class="{ 'invisible': !errors.has('nonField') }">
            {{ errors.first('nonField') }}
            (<a href="#" onclick="return false;" tabindex="0" data-toggle="popover" data-trigger="focus" title="Info"
                :data-content="targetNotVisibleInfoText">Info</a>)
          </div>
          <div class="alert alert-danger mt-3" role="alert" :class="{ 'invisible': !errors.has('expTime') }">
            {{ errors.first('expTime') }}
          </div>
        </div>
      </div>
      <div class="col-auto ml-auto">
        <Webcam class="pb-3" v-bind:telescope="telescope"></Webcam>
        <image-display :caption="'Most recent image'" :img-src="getThumbnail"
                       :popover-content="thumbnailInfoText"></image-display>
      </div>
    </div>
  </div>
</template>

<script>
  import moment from 'moment';
  import $ from 'jquery';
  import _ from 'lodash';
  import { Validator } from 'vee-validate';

  import 'rti.css';
  import Webcam from 'webcam.vue';
  import ImageDisplay from 'imageDisplay.vue';
  import {
    decimalRaToSexagesimal,
    decimalDecToSexagesimal,
    sexagesimalDecToDecimal,
    sexagesimalRaToDecimal,
    siteStates
  } from 'common';
  import { getButtonState } from 'feedback';
  import { cameraStates, progressStates } from '../js/common';

  const raValidation = {
    getMessage(field, params, data) {
      return data.message;
    },
    validate(value) {
      value = value.trim();

      let isValid = true;
      let errorReason = '';
      let convertedRa = sexagesimalRaToDecimal(value);
      if (!isNaN(value)) {
        if (value > 360 || value < 0) {
          isValid = false;
          errorReason = 'Decimal Right Ascension must be in the range 0 to 360 degrees';
        }
      }
      else if (!convertedRa) {
        isValid = false;
        errorReason = 'Right Ascension must be in decimal format [0 to 360] or sexigesimal format [00:00:00.0 to 23:59:59.9]';
      }
      else if (value === convertedRa) {
        isValid = false;
        errorReason = 'Sexigesimal Right Ascension must be in the range [00:00:00.0 to 23:59:59.9]';
      }

      return new Promise(resolve => {
        resolve({
          valid: isValid,
          data: {message: errorReason}
        });
      });
    }
  };

  const decValidation = {
    getMessage(field, params, data) {
      return data.message;
    },
    validate(value) {
      let isValid = true;
      let errorReason = '';
      let convertedDec = sexagesimalDecToDecimal(value);
      if (!isNaN(value)) {
        if (value > 90 || value < -90) {
          isValid = false;
          errorReason = 'Decimal Declination must be in the range -90 to 90 degrees';
        }
      }
      else if (!convertedDec) {
        isValid = false;
        errorReason = 'Declination must be in decimal format [-90 to 90] or sexigesimal format [DD:MM:SS.S]';
      }
      else if (value === convertedDec || convertedDec > 90 || convertedDec < -90) {
        isValid = false;
        errorReason = 'Sexigesimal Declination must be in the range [-90:00:00.0 - 90:00:00.0]';
      }

      return new Promise(resolve => {
        resolve({
          valid: isValid,
          data: {message: errorReason}
        });
      });
    }
  };
  Validator.extend('ra', raValidation);
  Validator.extend('dec', decValidation);

  export default {
    name: 'observationPanel',
    props: ['telescope', 'agentStatus', 'sessionActive', 'utcNow', 'rtiBridgeUrl', 'session'],
    components: {
      Webcam,
      ImageDisplay
    },
    data: function() {
      const defaultExposureTime = 30;
      return {
        modeSelection: 'manual',
        estimatedObservationLength: 0,
        // A list of the non-zero exposure times that a user has submitted. If the parallel
        // case, this will be a list of length one, containing the longest of the exposure times.
        lastSubmittedExposureTimes: [],
        whatsupSearchUrl: 'https://whatsup.lco.global/search/',
        whatsUpRefreshMin: 5,
        waitingForStatus: false,
        targets: new Map(),
        targetName: '',
        defaultExposureTime: defaultExposureTime,
        exposures: [
          {filter: 'ip', filterText: 'Red', exposureTime: null},
          {filter: 'rp', filterText: 'Green', exposureTime: null},
          {filter: 'gp', filterText: 'Blue', exposureTime: null},
        ],
        raDecimal: null,
        raSexagesimal: null,
        decDecimal: null,
        decSexagesimal: null,
        maxExposureSeconds: 600,
        // These overheads are made up. They are approximations that are about the length of what a single observation
        // feels like.
        overheads: {
          '0m4': {
            'slew': 20
          },
          '1m0': {
            'slew': 35
          },
          '2m0': {
            'slew': 60
          }
        },
        numberOfExposuresStarted: 0,
        buttonType: 'go',
        isGoButtonDisabled: false,
        isCancelButtonDisabled: false,
        lastInstrumentState: '',
        filtersHelpText: `First, select the filters you'd like to use, and then set the exposure time for each. As you
        modify exposure times, help text displaying the estimated observation length will update. When you are ready to
        observe, click GO!`,
        thumbnailInfoText: `This is a thumbnail of the most recent image taken. Note that the image shown here at first
        may be left over from a previous session.`,
        targetNotVisibleInfoText: `A target may not be visible because it is too close to the moon, if it is below the
        horizon, or if it is too low on the horizon for the telescope to point at.`
      };
    },
    mounted: function() {
      this.getTargetList();
      window.setInterval(
        this.getTargetList, (1000 * 60) * this.whatsUpRefreshMin
      );
      window.setInterval(
        this.updateButtonStatus, 1000
      );
      // When a user collapses an exposure time input, clear the exposure time for that filter
      for (let exposure of this.exposures) {
        $('#' + exposure.filterText + '-collapse').on('hidden.bs.collapse', function () {
          exposure.exposureTime = null;
        })
      }
    },
    beforeDestroy: function() {
      $('.popover').popover('dispose');
    },
    computed: {
      totalTime: function() {
        // An approximation of the total time that the observation will take, including slew, meant
        // to inform the user before actually clicking 'GO'.
        if (this.estimatedObservationLength <= 0) {
          return 0;
        }
        return Number(this.estimatedObservationLength) + this.getSlewOverhead();
      },
      buttonFeedback: function() {
        let buttonState = getButtonState(this.agentStatus, this.sessionActive);
        if (buttonState.feedback) {
          return buttonState.feedback;
        } else if (this.inputsAreNotAllFilledIn) {
          return 'The observation form must be filled out with the target name, Right Ascension, \
          Declination, and at least one exposure time in order to submit an observation';
        } else {
          return '';
        }
      },
      getThumbnail: function() {
        let agentImage = 'data:image/jpeg;base64, ' + this.agentStatus.thumbnail;
        let defaultImage = 'noImageAvailableYet.jpg';
        return this.agentStatus.thumbnail ? agentImage : defaultImage;
      },
      canObserve: function() {
        return (this.agentStatus.availability === siteStates.AVAILABLE && this.sessionActive);
      },
      isObserving: function() {
        return this.agentStatus.progress === progressStates.EXECUTING;
      },
      exposureTimes: function() {
        return _.map(this.exposures, function(exposure) {
            return Number(exposure.exposureTime);
          }
        );
      },
      atLeastOneExposureTimeSet: function() {
        // Returns whether at least one of the exposure times is set to a positive value.
        for (let exposureTime of this.exposureTimes) {
          if (exposureTime > 0) {
            return true;
          }
        }
        return false;
      },
      inputsAreNotAllFilledIn: function() {
        return !(this.targetName && this.raSexagesimal && this.decSexagesimal && this.atLeastOneExposureTimeSet);
      }
    },
    methods: {
      getSlewOverhead: function() {
        let telescopeClass = this.telescope.telescope.slice(0, 3);
        return this.overheads[telescopeClass].slew;
      },
      go: function() {
        this.isGoButtonDisabled = true;

        this.raDecimal = sexagesimalRaToDecimal(this.raSexagesimal);
        this.decDecimal = sexagesimalDecToDecimal(this.decSexagesimal);

        this.errors.clear();
        this.waitingForStatus = true;

        let expTimesList = this.exposureTimes;
        let expFiltersList = _.map(this.exposures, 'filter');

        let targetPayload = {
          name: this.targetName,
          ra: this.degreesToHours(this.raDecimal),
          dec: Number(this.decDecimal),
          expFilter: expFiltersList,
          expTime: expTimesList
        };

        let xhr = new XMLHttpRequest();
        xhr.open('POST', this.rtiBridgeUrl + '/command/go', true);
        xhr.setRequestHeader('Content-type', 'application/json');
        xhr.setRequestHeader('Authorization', this.session.token);
        console.log('Sending GO command with payload: ' + JSON.stringify(targetPayload));
        xhr.send(JSON.stringify(targetPayload));

        xhr.onload = () => {
          let response = JSON.parse(xhr.responseText);
          this.enableButton('go', 10);
          if (response.hasOwnProperty('errors')) {
            this.waitingForStatus = false;
            for (let errorKey in response.errors) {
              let errorBody = response.errors[errorKey][0];
              if (_.isPlainObject(errorBody)) {
                for (let index in errorBody) {
                  this.errors.add(errorKey + this.exposures[index].filter, errorBody[index][0]);
                }
              } else {
                this.errors.add(errorKey, errorBody);
              }
            }
            if (response.errors) {
              return;
            }
          }
          this.numberOfExposuresStarted = 0;
          let isParallel = _.get(response, 'is_parallel', false);
          this.setLastSubmittedExposureTimes(isParallel, expTimesList);
          this.sendLastSubmittedExposureTimes();
        };
      },
      getEstimatedObservationLength: _.debounce(function() {
        let emptyObservationLength = 0;
        if (this.atLeastOneExposureTimeSet) {
          let xhr = new XMLHttpRequest();
          xhr.open('POST', this.rtiBridgeUrl + '/observation-params', true);
          xhr.setRequestHeader('Content-type', 'application/json');
          xhr.setRequestHeader('Authorization', this.session.token);
          xhr.send(JSON.stringify({'expTime': this.exposureTimes}));
          xhr.onload = () => {
            let response = JSON.parse(xhr.responseText);
            this.estimatedObservationLength = response['observation_params']['observation_length'] || emptyObservationLength;
          };
        } else {
          this.estimatedObservationLength = emptyObservationLength;
        }
      }, 150),
      cancel: function() {
        this.isCancelButtonDisabled = true;
        this.waitingForStatus = true;

        console.log('Aborting observation.');
        let xhr = new XMLHttpRequest();
        xhr.open('POST', this.rtiBridgeUrl + '/command/stop', true);
        xhr.setRequestHeader('Content-type', 'application/json');
        xhr.setRequestHeader('Authorization', this.session.token);
        xhr.send();
        xhr.onload = () => {
          console.log(xhr.responseText);
          this.enableButton('cancel', 20);
        };
      },
      enableButton: function(buttonType, delaySeconds) {
        // Enable the specified button after a period of time. In the normal case, when
        // either button type is clicked, it should turn into the other type of button within
        // a few seconds. This is a hack to ensure that the buttons go back to being enabled
        // after having been disabled, in case the button type does not change because the frontend
        // missed an agent status transition, for example if the progress state errors out so quickly
        // that we don't actually see the transitional state because it happened between polls.
        delaySeconds = delaySeconds || 20;
        let that = this;
        window.setTimeout(function() {
          if (buttonType === 'cancel') {
            that.isCancelButtonDisabled = false;
          } else if (buttonType === 'go') {
            that.isGoButtonDisabled = false;
          }
        }, delaySeconds * 1000);
      },
      degreesToHours: function(degrees) {
        return degrees / 15;
      },
      getTargetList: function() {
        let start = moment.utc();
        let aperture = this.telescope.telescope.slice(0, 3);
        let that = this;
        $.ajax(
          this.whatsupSearchUrl,
          {
            dataType: 'jsonp',
            data: {
              'format': 'jsonp',
              'start': this.msToIsoUTCString(start),
              'aperture': aperture,
              'site': this.telescope.site
            },
            success: (data) => {
              that.targetList = data;

              for (let target of data.targets) {
                let filters = target.filters.reduce(
                  (filterMap, filter) => filterMap.set(filter.name, {exposureSeconds: filter.exposure}), new Map());
                let averageExposureSeconds = target.filters.reduce((sum, filter) => filter.exposure + sum, 0) /
                  target.filters.length;

                if (averageExposureSeconds > this.maxExposureSeconds)
                  continue;

                /* FIXME: Clear old target list before updating */
                this.targets.set(target.name, {
                  ra: target.ra,
                  dec: target.dec,
                  filters: filters,
                  avmDesc: target.avmdesc,
                  averageExposureSeconds: averageExposureSeconds
                });
              }
            }
          }
        );
      },
      msToIsoUTCString: function(time) {
        return moment(time).utcOffset(0).format('YYYY-MM-DDTHH:mm:ss');
      },
      updateButtonStatus: function() {
        let newInstrumentState = this.agentStatus.instrument.replace('_', ' ');
        if (newInstrumentState === cameraStates.EXPOSING && this.lastInstrumentState !== cameraStates.EXPOSING) {
          this.numberOfExposuresStarted += 1;
        }
        this.lastInstrumentState = newInstrumentState;

        if (this.agentStatus.progress === progressStates.READY && this.numberOfExposuresStarted >= this.lastSubmittedExposureTimes.length) {
          this.buttonType = 'go';
        } else if (this.agentStatus.progress === progressStates.ERROR) {
          this.buttonType = 'go';
        } else if (this.agentStatus.progress === '') {
          // This is what the agent status progress state is initialized to.
          this.buttonType = 'go';
        } else if (!this.sessionActive) {
          this.buttonType = 'go';
        } else {
          this.buttonType = 'cancel';
        }
      },
      setLastSubmittedExposureTimes: function(isParallel, expTimesList) {
        if (isParallel) {
          this.lastSubmittedExposureTimes = [_.max(expTimesList)];
        } else {
          this.lastSubmittedExposureTimes = _.filter(expTimesList, function(expTime) { return expTime > 0; });
        }
      },
      sendLastSubmittedExposureTimes: function() {
        let exposureTimesToSend = [];
        _.assign(exposureTimesToSend, this.lastSubmittedExposureTimes);
        this.$emit('sendSubmittedExposureTimes', exposureTimesToSend);
      },
      updateTargetInput: function() {
        if (this.modeSelection === 'list') {
          let targetInfo = this.targets.get(this.targetName);
          this.raDecimal = targetInfo.ra;
          this.raSexagesimal = decimalRaToSexagesimal(targetInfo.ra);
          this.decDecimal = targetInfo.dec;
          this.decSexagesimal = decimalDecToSexagesimal(targetInfo.dec);
          this.updateExposureTimesFromListTarget(targetInfo.filters);
        }
      },
      updateExposureTimesFromListTarget: function(targetInfoFilters) {
        let defaultExposureTime = this.defaultExposureTime;
        for (let exposureTime of targetInfoFilters.values()) {
          defaultExposureTime = exposureTime.exposureSeconds;
          break;
        }
        for (let exposure of this.exposures) {
          if (targetInfoFilters.has(exposure.filter)) {
            exposure.exposureTime = targetInfoFilters.get(exposure.filter).exposureSeconds;
          } else {
            exposure.exposureTime = defaultExposureTime;
          }
        }
      }
    },
    watch: {
      buttonType: function() {
        this.isGoButtonDisabled = false;
        this.isCancelButtonDisabled = false;
      },
      targetName: function() {
        this.updateTargetInput();
      },
      modeSelection: function(newModeSelectionValue) {
        // Show all the exposure time inputs when user switches to list mode because they are
        // populated with exposure times from the target list when a target is selected.
        if (newModeSelectionValue === 'list') {
          $('.filter-collapse').collapse('show');
          $('.filter-check').prop('checked', true)
        }
        if (!this.targets.has(this.targetName))
          return;

        this.updateTargetInput();
      },
      exposures: {
        deep: true,
        handler() {
          this.getEstimatedObservationLength();
        }
      }
    }
  };
  // Enable all tooltips in the document.
  $(document).ready(function() {
    $('[data-toggle="tooltip"]').tooltip({
      container: 'body'
    });
    $(function() {
      $('[data-toggle="popover"]').popover();
    });
    $('.popover-dismiss').popover({
      trigger: 'focus',
      title: 'Info'
    });
  });
</script>
